import { useMemo } from 'react';
import { PanoramaDetails } from '../services/UploadManagementServiceTypes';
import { WaypointOptions } from './TourModeModal';

export type IndexedPanorama = { panorama: PanoramaDetails; index: number };

export interface GalleryNavigation {
  showNavigationButtons: boolean;
  currentPanoramaIndex: number;
  previousPanoramaIndex: number;
  nextPanoramaIndex: number;
  navigablePanoramas: IndexedPanorama[];
}

const navigationPanoramasMapper = (panorama: PanoramaDetails, index: number): IndexedPanorama => ({
  panorama,
  index,
});

export const useGalleryNavigation = ({
  waypointsShown,
  panoramas,
  currentPanorama,
}: {
  panoramas: PanoramaDetails[];
  waypointsShown: WaypointOptions;
  currentPanorama?: PanoramaDetails;
}): GalleryNavigation => {
  const showNavigationButtons = waypointsShown === null || waypointsShown === 'none';
  const currentPanoramaIndex = panoramas.findIndex((p) => p === currentPanorama);
  const previousPanoramaIndex = (currentPanoramaIndex + panoramas.length - 1) % panoramas.length;
  const nextPanoramaIndex = (currentPanoramaIndex + 1) % panoramas.length;
  const panoramaCount = panoramas.length;
  const previousPanorama = panoramas[previousPanoramaIndex];
  const nextPanorama = panoramas[nextPanoramaIndex];

  const navigablePanoramas = useMemo(() => {
    if (waypointsShown === 'all' && panoramaCount > 2) {
      return panoramas
        .map(navigationPanoramasMapper)
        .filter(({ index }) => currentPanoramaIndex !== index);
    }
    if (waypointsShown === '2' && panoramaCount > 2) {
      return [
        navigationPanoramasMapper(previousPanorama, previousPanoramaIndex),
        navigationPanoramasMapper(nextPanorama, nextPanoramaIndex),
      ];
    }
    if ((waypointsShown === '2' || waypointsShown === 'all') && panoramaCount === 2) {
      return [navigationPanoramasMapper(previousPanorama, previousPanoramaIndex)];
    }
    return [];
  }, [
    waypointsShown,
    panoramaCount,
    previousPanorama,
    nextPanorama,
    previousPanoramaIndex,
    nextPanoramaIndex,
    panoramas,
    currentPanoramaIndex,
  ]);

  return {
    showNavigationButtons,
    previousPanoramaIndex,
    nextPanoramaIndex,
    navigablePanoramas,
    currentPanoramaIndex,
  };
};
